import { combineEpics, ofType } from "redux-observable";
import {
  switchMap,
  withLatestFrom,
  map,
  catchError,
  debounceTime,
  mergeMap,
  concatMap,
  distinctUntilChanged,
  take,
} from "rxjs/operators";
import * as calculationActions from "../../../../src/store/calculation/calculationSlice";
import { of, from, EMPTY } from "rxjs";
import * as pumpService from "../../../../src/services/calculations/calculation-output/pump.service";
import * as pumpActions from "../../../../src/store/calculation/calculation-output/pumpSlice";
import * as gasActions from "../../../../src/store/calculation/calculation-output/gasHandlerSlice";
import * as gasCalcActions from "../../../../src/store/calculation/calculation-output/gasCalculaionSlice";
import { RootState } from "../../../../src/store/ConfigureStore";
import {
  PumpInputCalculated,
  PumpPressureShaft,
} from "../../../../src/store/model/calculations/calculation";
import { setGasNotification } from "../../../store/calculation/calculation-output/gasCalculaionSlice";
import { setHiddenShaftType } from "../../../store/calculation/calculation-input/TubingSlice";
import { toast } from "react-toastify";

let calledFetchBOHEquipments = false;

const loadPumpList = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "tubing/setCatalogType",
      "basicCalculation/setBasicCalculationComplete",
      "pump/setPumpRequiredTdh",
      "pump/setPumpDesiredStage",
      "tubing/setHiddenPumpShowAll",
    ),
    // take(1),
    debounceTime(500),
    withLatestFrom(state$),
    distinctUntilChanged(([prevAction, prevState], [currAction, currState]) => {
      return JSON.stringify(prevState) === JSON.stringify(currState);
    }),
    concatMap(([action, state]) => {
      const {
        tubing,
        wellInput,
        fluid,
        pump,
        gasCalculations,
        basicCalculations,
      } = state;

      if (
        gasCalculations?.GasCalculationComplete &&
        basicCalculations?.BasicCalculationComplete
      ) {
        const postData = [
          tubing?.tab1Input?.casingOD,
          wellInput?.tab2Input?.productionRate,
          wellInput?.tab2Input?.unitProductionRateListVal,
          fluid?.tab3Input?.combinedSpGr,
          pump?.PumpInput?.pump_ReqdTDH || basicCalculations.basicTDHCalculation.tdh,
          tubing?.tab1Input?.unitSys,
          fluid?.tab3Input?.fluidViscosity || "0",
          fluid?.tab3Input?.unitFluidViscosityListVal,
          gasCalculations?.GasFinalCalculatedData?.preTFV ||
          gasCalculations?.GasCalculationOutPut?.Intake_TotalFluidVolume,
          tubing?.tab1Input?.hdnPmpShowAll === "1",
          tubing?.tab1Input?.catalogType,
        ];
        return from(pumpService.loadPumpData(postData)).pipe(
          mergeMap((data) => {
            const selectedPump = data.find((pump: any) => pump.pumpID === tubing?.tab1Input?.hiddenPumpID)
            return [
              pumpActions.setSelectedPump(selectedPump),
              pumpActions.setPumpList(data)
            ]
          }),
          catchError(() => of(console.error("Failed to load pump data")))
        );
      }

      return EMPTY;
    })
  );
};

const getPumpFreeGasWarning = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpChart1Data", "pump/setPumpList"),
    withLatestFrom(
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.pump?.SelectedPump)),
      state$.pipe(map((state: RootState) => state?.fluid))
    ),
    concatMap(
      ([actionData, gasOutput, tab1Input, selectedPump, fluidTabData]) => {
        if (selectedPump?.pumpID && gasOutput) {
          let fC_DischargePressure =
            gasOutput?.CP3_FreeGas ??
            gasOutput?.CP2_FreeGas ??
            gasOutput?.CP1_FreeGas ??
            gasOutput?.GKX_FreeGas ??
            gasOutput?.SP4_FreeGas ??
            gasOutput?.SP3_FreeGas ??
            gasOutput?.SP2_FreeGas ??
            gasOutput?.SP1_FreeGas ??
            0;

          let freeGas = 0;
          const pumpName = selectedPump?.pumpID;
          let unitSysVal = tab1Input?.unitSys;
          let gasAvail = fluidTabData?.gasAvailblty;
          if (gasAvail === "0") freeGas = gasOutput?.Intake_FreeGas;
          else freeGas = fC_DischargePressure;

          let postData = [freeGas, pumpName, unitSysVal];

          return from(pumpService?.loadPmpFreeGasCheck(postData)).pipe(
            map((data) => pumpActions?.setPumpFreeGasWarning(data || false))
          );
        }
        return EMPTY;
      }
    )
  );
};

const loadPumpLabels = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpList", "tubing/setHiddenPumpId"),
    withLatestFrom(state$),
    distinctUntilChanged(([prevAction, prevState], [currAction, currState]) => {
      return JSON.stringify(prevState) === JSON.stringify(currState);
    }),
    concatMap(([action, state]) => {
      const {
        tubing,
        wellInput,
        fluid,
        pump,
        gasCalculations,
        basicCalculations,
      } = state;
      const hiddenPumpID = tubing?.tab1Input?.hiddenPumpID;
      if (hiddenPumpID) {
        const isValidCorrectionFactorsAvailable =
          +pump?.PumpInputCalculated?.flwCrrctnForPump > 0 &&
          +pump?.PumpInputCalculated?.hdCrrctnForPump > 0 &&
          +pump?.PumpInputCalculated?.pWRCrrctnForPump > 0;

        const postData = {
          CorrectionFactors: isValidCorrectionFactorsAvailable
            ? {
              FlowCorrection:
                +pump?.PumpInputCalculated?.flwCrrctnForPump / 100,
              HeadCorrection: +pump?.PumpInputCalculated?.hdCrrctnForPump / 100,
              PowerCorrection:
                +pump?.PumpInputCalculated?.pWRCrrctnForPump / 100,
            }
            : null,
          PumpId: hiddenPumpID,
          Frequency: +fluid?.tab3Input?.operatngFreq,
          Speed: +pump?.PumpInput?.pump_FullLoadSp || 3500,
          TFV:
            +gasCalculations?.GasFinalCalculatedData?.preTFV ||
            +gasCalculations?.GasCalculationOutPut?.Intake_TotalFluidVolume ||
            0,
          CompositeSpGravity: +fluid?.tab3Input?.combinedSpGr || 0,
          DesiredStages: +pump?.PumpInput?.pump_DesrdStges || 0,
          Viscosity: +fluid?.tab3Input?.fluidViscosity || 0,
          ViscosityUnit: +fluid?.tab3Input?.unitFluidViscosityListVal || 0,
          TDH: +basicCalculations?.basicTDHCalculation?.tdh || 0,
          UnitSystem: +tubing?.tab1Input?.unitSys || 0,
          IsPumpChanged: action?.type === "tubing/setHiddenPumpId",
        };

        return from(pumpService?.loadPumpCalcOutputLables(postData)).pipe(
          mergeMap((data: any) => {
            const pumpInputCalculated = {
              flwCrrctnForPump: data?.flowCorrection?.toString() || "0",
              hdCrrctnForPump: data?.headCorrection?.toString() || "0",
              pWRCrrctnForPump: data?.powerCorrection?.toString() || "0",
              hPOPPump: data?.hp?.toString() || "0",
              minNoOfStages: data?.minNoStages?.toString() || "0",
              ftLftPerStage: data?.liftPerStage?.toString() || "0",
              pmpFinalTDH: data?.finalTDH?.toString() || "0",
              hPOPPump60Hz: data?.hP60?.toString() || "0",
              efficiency: data?.efficiency?.toString() || "0",
            };

            return [
              pumpActions?.setPumpInputCalculated(pumpInputCalculated),
            ];
          }),
          catchError((error) => {
            console.error("Failed to load pump calculation data", error);
            return [pumpActions?.setPumpInputCalculated({})];
          })
        );
      }
      return of({ type: "pump/setPumpInputCalculated", payload: {} });;
    })
  );
};

const loadPumpLablesOnCorrectionFactorsOverride = (
  action$: any,
  state$: any
) => {
  return action$.pipe(
    ofType("pump/setPumpOverrideCorrectionFactors"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(
        map(
          (state: RootState) => state?.gasCalculations?.GasFinalCalculatedData
        )
      )
    ),
    switchMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        pumpInput,
        basicTdhData,
        gasOutput,
        finalData,
      ]) => {
        let postData = {
          CorrectionFactors: {
            FlowCorrection: actionData?.payload?.flowCorrection / 100,
            HeadCorrection: actionData?.payload?.headCorrection / 100,
            PowerCorrection: actionData?.payload?.powerCorrection / 100,
          },
          PumpId: tab1Input?.hiddenPumpID,
          Frequency: +tab3Input?.operatngFreq,
          Speed: +pumpInput?.pump_FullLoadSp || 3500,
          TFV: +finalData?.preTFV || +gasOutput?.Intake_TotalFluidVolume || 0,
          CompositeSpGravity: +tab3Input?.combinedSpGr || 0,
          DesiredStages: +pumpInput?.pump_DesrdStges || 0,
          Viscosity: +tab3Input?.fluidViscosity || 0,
          ViscosityUnit: +tab3Input?.unitFluidViscosityListVal || 0,
          TDH: +basicTdhData?.tdh || 0,
          UnitSystem: +tab1Input?.unitSys || 0,
          IsPumpChanged: false,
        };

        return from(
          pumpService?.loadPumpCalcOutputForCorrectionFactorOverride(postData)
        ).pipe(
          map((data) => {
            let pumpInputCalculated: PumpInputCalculated = {
              flwCrrctnForPump: data?.flowCorrection?.toString(),
              hdCrrctnForPump: data?.headCorrection?.toString(),
              pWRCrrctnForPump: data?.powerCorrection?.toString(),
              hPOPPump: data?.hp?.toString(),
              minNoOfStages: data?.minNoStages?.toString(),
              ftLftPerStage: data?.liftPerStage?.toString(),
              pmpFinalTDH: data?.finalTDH?.toString(),
              hPOPPump60Hz: data?.hP60?.toString(),
              efficiency: data?.efficiency?.toString(),
            };

            return {
              pumpInputCalculated: pumpInputCalculated,
              desirdNoOfStges: data?.desiredStages,
            };
          }),
          mergeMap((data) => [
            pumpActions?.setPumpDesiredStage(data?.desirdNoOfStges),
            pumpActions?.setPumpInputCalculated(data?.pumpInputCalculated),
          ])
        );
      }
    )
  );
};

const loadPumpiterations = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),

      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(
        map(
          (state: RootState) => state?.gasCalculations?.GasFinalCalculatedData
        )
      ),
      state$.pipe(map((state: RootState) => state?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.fluid?.regionList)),
      state$.pipe(map((state: RootState) => state?.fluid?.selectedRegion)),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled))
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        pumpInput,
        pumpInputCalculated,
        basicTdhData,
        basicGasCalculation,
        finalData,
        region,
        regionList,
        selectedRegion,
        aiEnabled
      ]) => {
        if (tab1Input?.hiddenPumpID && basicGasCalculation && finalData) {
          const selRegion = regionList?.find((x: any) => x?.id === region);
          let BOPD = tab3Input?.bopd?.toString() || "0";
          let BWPD = tab3Input?.bwpd?.toString() || "0";
          let bottmWhole = tab3Input?.bottomHoleTemp?.toString() || "0";
          let compSPGr = tab3Input?.combinedSpGr?.toString() || "0";
          let GOR = tab3Input?.gor?.toString() || "0";
          let GasComprs = tab3Input?.gasCompressibility?.toString() || "0";
          let gasDesign = basicGasCalculation?.gasDesign?.toString() || "0";
          let oilApi = tab3Input?.oilAPI?.toString() || "0";
          let PIP = basicTdhData?.pip?.toString() || "0";
          let prodRate = tab2Input?.productionRate?.toString() || "0";
          let spGrGas = tab3Input?.spGrGas?.toString() || "0";
          let spGrOil = tab3Input?.spGrOil?.toString() || "0";
          let spGrWatr = tab3Input?.spGrWater?.toString() || "0";
          let tubingOD = tab1Input?.tubingOD?.toString() || "0";
          let watrCut = tab3Input?.waterCut?.toString() || "0";
          let bopdUnit = tab3Input?.unitBOPDListVal;
          let bwpdUnit = tab3Input?.unitBWPDListVal;
          let bottmWholeUnit = tab3Input?.unitBottomHoleTempListVal;
          let gasAvail = tab3Input?.gasAvailblty?.toString() || "0";
          let bubbPres = tab3Input?.bubblePoint?.toString() || "0";

          if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
          }
          let GORUnit = tab3Input?.unitGORListVal;
          let prodctnRateUnit = tab2Input?.unitProductionRateListVal;
          let unitSys = tab1Input?.unitSys;
          let casingPres = tab2Input?.casingPressure?.toString() || "0";
          let casingPresUnit =
            tab2Input?.unitCasingPressureListVal?.toString() || "";
          let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
          let perfrtn = tab2Input?.perforation?.toString() || "0";
          let perfrtnUnit = tab2Input?.unitPerforationListVal;
          let bubbPresUnit = tab3Input?.unitBubblePointListVal;
          let pumpSetng = tab2Input?.tvd === null ? "0" : tab2Input?.tvd;
          let pumpSetngUnit = tab2Input?.unitTVDListVal;
          let pmpID = tab1Input?.hiddenPumpID ?? "0";
          let desrdNoOfStge = pumpInput?.pump_DesrdStges?.toString();
          if (
            desrdNoOfStge === "" ||
            desrdNoOfStge == null ||
            desrdNoOfStge === undefined
          ) {
            desrdNoOfStge = "0";
          }
          let headPrStage = pumpInputCalculated?.ftLftPerStage ?? "0";
          let tempUnit = tab1Input?.hdnUnitMotor_T1OperatingTemp;
          let GasAvail = tab3Input?.gasAvailblty?.toString() || "";
          let prodRateUnit = tab2Input?.unitProductionRateListVal;

          let flowCorrection = +pumpInputCalculated?.flwCrrctnForPump / 100;
          let headCorrection = +pumpInputCalculated?.hdCrrctnForPump / 100;
          let powerCorrection = +pumpInputCalculated?.pWRCrrctnForPump / 100;

          let pmpEff = +pumpInputCalculated?.efficiency;
          let isGasVAl = GasAvail === "1";
          let opFreq = tab3Input?.operatngFreq;

          let preTFV = finalData?.preTFV ?? "";
          let finPIP = finalData?.finPIP ?? "";
          let temp = finalData?.temp ?? "";
          let BG = finalData?.BG ?? "";
          let GasVolm = finalData?.GasVolm ?? "";
          GOR = finalData?.GOR ?? GOR;
          compSPGr = finalData?.compSPGr ?? compSPGr;
          FLOP = +finalData?.FLOP;
          let pumpId = tab1Input?.hiddenPumpID;
          let regionName = selRegion?.name ?? selectedRegion ?? '';
          let staticBHHd = 0;
          let inflowType = +(tab2Input?.inflowTypeLstVal || 0);
          let isGasHandler = false;
          let AIEnabled = aiEnabled;

          let staticBHP = tab2Input?.sbhpForPressre ?? 0;
          if (inflowType === 0) {
            staticBHP = tab2Input?.reservoirPressure;
            staticBHHd = tab2Input?.staticFluidLvl;
          } else if (inflowType === 2) {
            staticBHHd = tab2Input?.staticFluidLvlForHd;
          }

          let postInputSepData: any = [
            BOPD,
            BWPD,
            bottmWhole,
            compSPGr,
            GOR,
            gasDesign,
            oilApi,
            PIP,
            prodRate,
            spGrGas,
            spGrOil,
            spGrWatr,
            tubingOD,
            watrCut,
            bopdUnit,
            bwpdUnit,
            bottmWholeUnit,
            GORUnit,
            prodctnRateUnit,
            unitSys,
            casingPres,
            casingPresUnit,
            FLOP,
            GasComprs,
            isGasVAl,
            perfrtn,
            perfrtnUnit,
            bubbPres,
            bubbPresUnit,
            pumpSetng,
            pumpSetngUnit,
            preTFV,
            finPIP,
            pmpID,
            desrdNoOfStge,
            headPrStage,
            prodRateUnit,
            pmpEff,
            temp,
            tempUnit,
            opFreq,
            BG,
            GasVolm,
            regionName,
            staticBHHd,
            inflowType,
            staticBHP,
            pumpId,
            flowCorrection,
            headCorrection,
            powerCorrection,
            isGasHandler,
            AIEnabled
          ];

          return from(pumpService?.loadPumpIteration(postInputSepData)).pipe(
            map((data) => {
              return pumpActions?.setPumpIteration(data);
            })
          );
        }
        return of(pumpActions?.setPumpIteration([]));
      }
    )
  );
};



const calculationsPumpAIData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "pump/setPumpIteration"
    ),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.PumpIteration)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.SelectedPump)),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
    ),
    concatMap(([actionData, pumpIteration, tab3Input, selectedPump, AIEnabled, pumpInput]) => {

      let pumpName = selectedPump?.pumpName;
      if (AIEnabled && pumpIteration && pumpIteration?.length > 0 && pumpName?.toUpperCase()?.includes("UNB")
        && !pumpName?.toUpperCase()?.includes("UNB7.5") && !pumpName?.toUpperCase()?.includes("75")) {
        let tfv = pumpIteration?.[0]?.chargePumpSepTFV;
        let gvf = pumpIteration?.[0]?.chargePumpSepFreeGas;
        let frequency = tab3Input?.operatngFreq;

        let pumpStages = pumpInput?.pump_DesrdStges?.toString();
        const postData = [pumpName, gvf, frequency, tfv];

        return from(pumpService.getAIPumpData(postData)).pipe(
          mergeMap(data => {
            if (data && data?.successFlag) {
              let finalTDH = data?.headPerStageFt * pumpStages;
              let power = data?.powerPerStageHp * pumpStages;
              return [
                pumpActions?.setPumpFinalTDH(finalTDH.toFixed(2)),
                pumpActions?.setPumpPower(power.toFixed(2))
              ]
            }
            else if (data && !data?.successFlag) {
              toast.warning("AI Gas Modifier failed to generate results. Please check your input and try again. If the issue continues, contact support.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                style: {
                  background: "#f04438",
                  color: "#fff",
                }
              });
              return of(calculationActions.setAIEnabled(false));
            }
            else
              return of({ type: "" });
          }),
        )
      }
      return of({ type: "" });
    }),
  )
};

const calculationsCP1AIData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "pump/setChargePump1Iteration"
    ),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.ChargePump1Iteration)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.chargePumpName)),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.BasicGasCalculation?.chargePumpDesrdStge)),
    ),
    concatMap(([actionData, pumpIteration, tab3Input, selectedCP1, AIEnabled, pumpStg]) => {

      let pumpName = selectedCP1;
      if (AIEnabled && pumpIteration && pumpIteration?.length > 0 && pumpName?.toUpperCase()?.includes("UNB")
        && !pumpName?.toUpperCase()?.includes("UNB7.5") && !pumpName?.toUpperCase()?.includes("75")) {
        let tfv = pumpIteration?.[0]?.chargePumpSepTFV;
        let gvf = pumpIteration?.[0]?.chargePumpSepFreeGas;
        let frequency = tab3Input?.operatngFreq;

        let pumpStages = pumpStg;
        const postData = [pumpName, gvf, frequency, tfv];

        return from(pumpService.getAIPumpData(postData)).pipe(
          mergeMap(data => {
            if (data && data?.successFlag) {
              let finalTDH = data?.headPerStageFt * pumpStages;
              let power = data?.powerPerStageHp * pumpStages;
              return [
                gasActions?.setCP1FinalTDH(finalTDH.toFixed(2)),
                gasCalcActions?.setCP1Power(power.toFixed(2))
              ]
            }
            else if (data && !data?.successFlag) {
              toast.warning("AI Gas Modifier failed to generate results. Please check your input and try again. If the issue continues, contact support.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                style: {
                  background: "#f04438",
                  color: "#fff",
                }
              });
              return of(calculationActions.setAIEnabled(false));
            }
            else
              return of({ type: "" });
          }),
        )
      }
      return of({ type: "" });
    }),
  )
};

const calculationsCP2AIData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "pump/setChargePump2Iteration"
    ),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.ChargePump2Iteration)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.chargePump2Name)),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.BasicGasCalculation?.chargePumpDesrdStge2)),
    ),
    concatMap(([actionData, pumpIteration, tab3Input, selectedCP2, AIEnabled, pumpStg]) => {

      let pumpName = selectedCP2;
      if (AIEnabled && pumpIteration && pumpIteration?.length > 0 && pumpName?.toUpperCase()?.includes("UNB")
        && !pumpName?.toUpperCase()?.includes("UNB7.5") && !pumpName?.toUpperCase()?.includes("75")) {
        let tfv = pumpIteration?.[0]?.chargePumpSepTFV;
        let gvf = pumpIteration?.[0]?.chargePumpSepFreeGas;
        let frequency = tab3Input?.operatngFreq;

        let pumpStages = pumpStg;
        const postData = [pumpName, gvf, frequency, tfv];

        return from(pumpService.getAIPumpData(postData)).pipe(
          mergeMap(data => {
            if (data && data?.successFlag) {
              let finalTDH = data?.headPerStageFt * pumpStages;
              let power = data?.powerPerStageHp * pumpStages;
              return [
                gasActions?.setCP2FinalTDH(finalTDH.toFixed(2)),
                gasCalcActions?.setCP2Power(power.toFixed(2))
              ]
            }
            else if (data && !data?.successFlag) {
              toast.warning("AI Gas Modifier failed to generate results. Please check your input and try again. If the issue continues, contact support.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                style: {
                  background: "#f04438",
                  color: "#fff",
                }
              });
              return of(calculationActions.setAIEnabled(false));
            }
            else
              return of({ type: "" });
          }),
        )
      }
      return of({ type: "" });
    }),
  )
};

const calculationsCP3AIData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "pump/setChargePump3Iteration"
    ),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.ChargePump3Iteration)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.chargePump3Name)),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.BasicGasCalculation?.chargePumpDesrdStge3)),
    ),
    concatMap(([actionData, pumpIteration, tab3Input, selectedCP3, AIEnabled, pumpStg]) => {

      let pumpName = selectedCP3;
      if (AIEnabled && pumpIteration && pumpIteration?.length > 0 && pumpName?.toUpperCase()?.includes("UNB")
        && !pumpName?.toUpperCase()?.includes("UNB7.5") && !pumpName?.toUpperCase()?.includes("75")) {
        let tfv = pumpIteration?.[0]?.chargePumpSepTFV;
        let gvf = pumpIteration?.[0]?.chargePumpSepFreeGas;
        let frequency = tab3Input?.operatngFreq;

        let pumpStages = pumpStg;
        const postData = [pumpName, gvf, frequency, tfv];

        return from(pumpService.getAIPumpData(postData)).pipe(
          mergeMap(data => {
            if (data && data?.successFlag) {
              let finalTDH = data?.headPerStageFt * pumpStages;
              let power = data?.powerPerStageHp * pumpStages;
              return [
                gasActions?.setCP3FinalTDH(finalTDH.toFixed(2)),
                gasCalcActions?.setCP3Power(power.toFixed(2))
              ]
            }
            else if (data && !data?.successFlag) {
              toast.warning("AI Gas Modifier failed to generate results. Please check your input and try again. If the issue continues, contact support.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                style: {
                  background: "#f04438",
                  color: "#fff",
                }
              });
              return of(calculationActions.setAIEnabled(false));
            }
            else
              return of({ type: "" });
          }),
        )
      }
      return of({ type: "" });
    }),
  )
};

const loadGKXPumpiterations = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("gasHandlerCalculation/setGKXCalculationData"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasHandler?.GKXCalculationData)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(map((state: RootState) => state?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.fluid?.regionList)),
      state$.pipe(map((state: RootState) => state?.fluid?.selectedRegion))
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        basicTdhData,
        basicGasCalculation,
        gkxData,
        gasoutput,
        region,
        regionList,
        selectedRegion
      ]) => {
        if (
          basicGasCalculation &&
          basicGasCalculation?.useOfChgPmp === "1" &&
          basicGasCalculation?.chargePumpGKX?.length
        ) {
          const selRegion = regionList?.find((x: any) => x?.id === region);
          let BOPD = tab3Input?.bopd?.toString() || "0";
          let BWPD = tab3Input?.bwpd?.toString() || "0";
          let bottmWhole = tab3Input?.bottomHoleTemp?.toString() || "0";
          let compSPGr = tab3Input?.combinedSpGr?.toString() || "0";
          let GOR = tab3Input?.gor?.toString() || "0";
          let GasComprs = tab3Input?.gasCompressibility?.toString() || "0";
          let gasDesign = basicGasCalculation?.gasDesign?.toString() || "0";
          let oilApi = tab3Input?.oilAPI?.toString() || "0";
          let PIP = basicTdhData?.pip?.toString() || "0";
          let prodRate = tab2Input?.productionRate?.toString() || "0";
          let spGrGas = tab3Input?.spGrGas?.toString() || "0";
          let spGrOil = tab3Input?.spGrOil?.toString() || "0";
          let spGrWatr = tab3Input?.spGrWater?.toString() || "0";
          let tubingOD = tab1Input?.tubingOD?.toString() || "0";
          let watrCut = tab3Input?.waterCut?.toString() || "0";
          let bopdUnit = tab3Input?.unitBOPDListVal;
          let bwpdUnit = tab3Input?.unitBWPDListVal;
          let bottmWholeUnit = tab3Input?.unitBottomHoleTempListVal;
          let gasAvail = tab3Input?.gasAvailblty?.toString() || "0";
          let bubbPres = tab3Input?.bubblePoint?.toString() || "0";

          if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
          }
          let GORUnit = tab3Input?.unitGORListVal;
          let prodctnRateUnit = tab2Input?.unitProductionRateListVal;
          let unitSys = tab1Input?.unitSys;
          let casingPres = tab2Input?.casingPressure?.toString() || "0";
          let casingPresUnit =
            tab2Input?.unitCasingPressureListVal?.toString() || "";
          let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
          let perfrtn = tab2Input?.perforation?.toString() || "0";
          let perfrtnUnit = tab2Input?.unitPerforationListVal;
          let bubbPresUnit = tab3Input?.unitBubblePointListVal;
          let pumpSetng = tab2Input?.tvd === null ? "0" : tab2Input?.tvd;
          let pumpSetngUnit = tab2Input?.unitTVDListVal;
          let pmpID = tab1Input?.hiddenPumpID ?? "0";
          let desrdNoOfStge = gkxData?.desiredStages?.toString();
          if (
            desrdNoOfStge === "" ||
            desrdNoOfStge == null ||
            desrdNoOfStge === undefined
          ) {
            desrdNoOfStge = "0";
          }
          let headPrStage = gkxData?.liftPerStage ?? "0";
          let tempUnit = tab1Input?.hdnUnitMotor_T1OperatingTemp;
          let GasAvail = tab3Input?.gasAvailblty?.toString() || "";
          let prodRateUnit = tab2Input?.unitProductionRateListVal;

          let flowCorrection = +gasoutput?.GKX_FlowCorrection / 100 || 0;
          let powerCorrection = +gasoutput?.GKX_PowerCorrection / 100 || 0;
          let headCorrection = +gasoutput?.GKX_HeadCorrection / 100 || 0;

          let pmpEff = +gkxData?.efficiency || 0;
          let isGasVAl = GasAvail === "1";
          let opFreq = tab3Input?.operatngFreq;
          let preTFV = "";
          let finPIP = "";
          let temp = "";
          let BG = "";
          let GasVolm = "";

          const noOfGasSeps = basicGasCalculation?.noOfGasSeps;
          const gasSeperator = basicGasCalculation?.sepSelcted;
          if (+noOfGasSeps > 0 && gasSeperator) {
            if (noOfGasSeps === "1") {
              preTFV = gasoutput?.SP1_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP1_Pressure?.toString() ?? "";
              temp = gasoutput?.SP1_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP1_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP1_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP1_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP1_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP1_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "2") {
              preTFV = gasoutput?.SP2_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP2_Pressure?.toString() ?? "";
              temp = gasoutput?.SP2_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP2_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP2_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP2_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP2_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP2_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "3") {
              preTFV = gasoutput?.SP3_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP3_Pressure?.toString() ?? "";
              temp = gasoutput?.SP3_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP3_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP3_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP3_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP3_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP3_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "4") {
              preTFV = gasoutput?.SP4_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP4_Pressure?.toString() ?? "";
              temp = gasoutput?.SP4_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP4_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP4_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP4_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP4_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP4_FinalFLOP ?? FLOP);
            }
          } else {
            preTFV = gasoutput?.Intake_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.Intake_Pressure?.toString() ?? "";
            temp = gasoutput?.Intake_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.Intake_Bg?.toString() ?? "";
            GasVolm = gasoutput?.Intake_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.Intake_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.Intake_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.Intake_FinalFLOP ?? FLOP);
          }

          let pumpId = basicGasCalculation?.chargePumpGKX;
          let regionName = selRegion?.name ?? selectedRegion ?? '';
          let staticBHHd = 0;
          let inflowType = +(tab2Input?.inflowTypeLstVal || 0);
          let isGasHandler = true;
          let AIEnabled = false;

          let staticBHP = tab2Input?.sbhpForPressre ?? 0;
          if (inflowType === 0) {
            staticBHP = tab2Input?.reservoirPressure;
            staticBHHd = tab2Input?.staticFluidLvl;
          } else if (inflowType === 2) {
            staticBHHd = tab2Input?.staticFluidLvlForHd;
          }

          let postInputSepData: any = [
            BOPD,
            BWPD,
            bottmWhole,
            compSPGr,
            GOR,
            gasDesign,
            oilApi,
            PIP,
            prodRate,
            spGrGas,
            spGrOil,
            spGrWatr,
            tubingOD,
            watrCut,
            bopdUnit,
            bwpdUnit,
            bottmWholeUnit,
            GORUnit,
            prodctnRateUnit,
            unitSys,
            casingPres,
            casingPresUnit,
            FLOP,
            GasComprs,
            isGasVAl,
            perfrtn,
            perfrtnUnit,
            bubbPres,
            bubbPresUnit,
            pumpSetng,
            pumpSetngUnit,
            preTFV,
            finPIP,
            pmpID,
            desrdNoOfStge,
            headPrStage,
            prodRateUnit,
            pmpEff,
            temp,
            tempUnit,
            opFreq,
            BG,
            GasVolm,
            regionName,
            staticBHHd,
            inflowType,
            staticBHP,
            pumpId,
            flowCorrection,
            headCorrection,
            powerCorrection,
            isGasHandler,
            AIEnabled
          ];
          return from(pumpService?.loadPumpIteration(postInputSepData)).pipe(
            map((data) => {
              return pumpActions?.setGKXPumpIteration(data)
            })

          );
        }
        return of(pumpActions?.setGKXPumpIteration([]));
      }
    )
  );
};

const loadChargePump1iterations = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("gasHandlerCalculation/setCP1CalculationData"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(map((state: RootState) => state?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.fluid?.regionList)),
      state$.pipe(map((state: RootState) => state?.fluid?.selectedRegion)),
      state$.pipe(
        map((state: RootState) => state?.gasHandler?.CP1CalculationData)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled))
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        basicTdhData,
        basicGasCalculation,
        region,
        regionList,
        selectedRegion,
        cp1Data,
        gasoutput,
        aiEnabled
      ]) => {
        if (
          basicGasCalculation &&
          basicGasCalculation?.useOfChgPmpNew === "1" &&
          basicGasCalculation?.chargePump
        ) {
          const selRegion = regionList?.find((x: any) => x?.id === region);
          let BOPD = tab3Input?.bopd?.toString() || "0";
          let BWPD = tab3Input?.bwpd?.toString() || "0";
          let bottmWhole = tab3Input?.bottomHoleTemp?.toString() || "0";
          let compSPGr = tab3Input?.combinedSpGr?.toString() || "0";
          let GOR = tab3Input?.gor?.toString() || "0";
          let GasComprs = tab3Input?.gasCompressibility?.toString() || "0";
          let gasDesign = basicGasCalculation?.gasDesign?.toString() || "0";
          let oilApi = tab3Input?.oilAPI?.toString() || "0";
          let PIP = basicTdhData?.pip?.toString() || "0";
          let prodRate = tab2Input?.productionRate?.toString() || "0";
          let spGrGas = tab3Input?.spGrGas?.toString() || "0";
          let spGrOil = tab3Input?.spGrOil?.toString() || "0";
          let spGrWatr = tab3Input?.spGrWater?.toString() || "0";
          let tubingOD = tab1Input?.tubingOD?.toString() || "0";
          let watrCut = tab3Input?.waterCut?.toString() || "0";
          let bopdUnit = tab3Input?.unitBOPDListVal;
          let bwpdUnit = tab3Input?.unitBWPDListVal;
          let bottmWholeUnit = tab3Input?.unitBottomHoleTempListVal;
          let gasAvail = tab3Input?.gasAvailblty?.toString() || "0";
          let bubbPres = tab3Input?.bubblePoint?.toString() || "0";

          if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
          }
          let GORUnit = tab3Input?.unitGORListVal;
          let prodctnRateUnit = tab2Input?.unitProductionRateListVal;
          let unitSys = tab1Input?.unitSys;
          let casingPres = tab2Input?.casingPressure?.toString() || "0";
          let casingPresUnit =
            tab2Input?.unitCasingPressureListVal?.toString() || "";
          let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
          let perfrtn = tab2Input?.perforation?.toString() || "0";
          let perfrtnUnit = tab2Input?.unitPerforationListVal;
          let bubbPresUnit = tab3Input?.unitBubblePointListVal;
          let pumpSetng = tab2Input?.tvd === null ? "0" : tab2Input?.tvd;
          let pumpSetngUnit = tab2Input?.unitTVDListVal;
          let pmpID = tab1Input?.hiddenPumpID ?? "0";
          let desrdNoOfStge = cp1Data?.desiredStages?.toString();
          if (
            desrdNoOfStge === "" ||
            desrdNoOfStge == null ||
            desrdNoOfStge === undefined
          ) {
            desrdNoOfStge = "0";
          }
          let headPrStage = cp1Data?.liftPerStage ?? "0";
          let tempUnit = tab1Input?.hdnUnitMotor_T1OperatingTemp;
          let GasAvail = tab3Input?.gasAvailblty?.toString() || "";
          let prodRateUnit = tab2Input?.unitProductionRateListVal;

          let flowCorrection = +gasoutput?.CP1_FlowCorrection / 100 || 0;
          let powerCorrection = +gasoutput?.CP1_PowerCorrection / 100 || 0;
          let headCorrection = +gasoutput?.CP1_HeadCorrection / 100 || 0;

          let pmpEff = +(cp1Data?.efficiency ?? 0);
          let isGasVAl = GasAvail === "1";
          let opFreq = tab3Input?.operatngFreq;
          let preTFV = "";
          let finPIP = "";
          let temp = "";
          let BG = "";
          let GasVolm = "";

          const noOfGasSeps = basicGasCalculation?.noOfGasSeps;
          const gasSeperator = basicGasCalculation?.sepSelcted;
          if (
            basicGasCalculation?.useOfChgPmp &&
            basicGasCalculation?.chargePumpGKX
          ) {
            preTFV = gasoutput?.GKX_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.GKX_Pressure?.toString() ?? "";
            temp = gasoutput?.GKX_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.GKX_Bg?.toString() ?? "";
            GasVolm = gasoutput?.GKX_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.GKX_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.GKX_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.GKX_FinalFLOP ?? FLOP);
          } else if (+noOfGasSeps > 0 && gasSeperator) {
            if (noOfGasSeps === "1") {
              preTFV = gasoutput?.SP1_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP1_Pressure?.toString() ?? "";
              temp = gasoutput?.SP1_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP1_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP1_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP1_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP1_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP1_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "2") {
              preTFV = gasoutput?.SP2_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP2_Pressure?.toString() ?? "";
              temp = gasoutput?.SP2_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP2_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP2_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP2_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP2_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP2_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "3") {
              preTFV = gasoutput?.SP3_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP3_Pressure?.toString() ?? "";
              temp = gasoutput?.SP3_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP3_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP3_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP3_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP3_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP3_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "4") {
              preTFV = gasoutput?.SP4_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP4_Pressure?.toString() ?? "";
              temp = gasoutput?.SP4_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP4_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP4_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP4_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP4_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP4_FinalFLOP ?? FLOP);
            }
          } else {
            preTFV = gasoutput?.Intake_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.Intake_Pressure?.toString() ?? "";
            temp = gasoutput?.Intake_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.Intake_Bg?.toString() ?? "";
            GasVolm = gasoutput?.Intake_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.Intake_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.Intake_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.Intake_FinalFLOP ?? FLOP);
          }

          let pumpId = basicGasCalculation?.chargePump;
          let regionName = selRegion?.name ?? selectedRegion ?? '';
          let staticBHHd = 0;
          let inflowType = +(tab2Input?.inflowTypeLstVal || 0);
          let isGasHandler = false;
          let AIEnabled = aiEnabled;

          let staticBHP = tab2Input?.sbhpForPressre ?? 0;
          if (inflowType === 0) {
            staticBHP = tab2Input?.reservoirPressure;
            staticBHHd = tab2Input?.staticFluidLvl;
          } else if (inflowType === 2) {
            staticBHHd = tab2Input?.staticFluidLvlForHd;
          }

          let postInputSepData: any = [
            BOPD,
            BWPD,
            bottmWhole,
            compSPGr,
            GOR,
            gasDesign,
            oilApi,
            PIP,
            prodRate,
            spGrGas,
            spGrOil,
            spGrWatr,
            tubingOD,
            watrCut,
            bopdUnit,
            bwpdUnit,
            bottmWholeUnit,
            GORUnit,
            prodctnRateUnit,
            unitSys,
            casingPres,
            casingPresUnit,
            FLOP,
            GasComprs,
            isGasVAl,
            perfrtn,
            perfrtnUnit,
            bubbPres,
            bubbPresUnit,
            pumpSetng,
            pumpSetngUnit,
            preTFV,
            finPIP,
            pmpID,
            desrdNoOfStge,
            headPrStage,
            prodRateUnit,
            pmpEff,
            temp,
            tempUnit,
            opFreq,
            BG,
            GasVolm,
            regionName,
            staticBHHd,
            inflowType,
            staticBHP,
            pumpId,
            flowCorrection,
            headCorrection,
            powerCorrection,
            isGasHandler,
            AIEnabled
          ];

          return from(pumpService?.loadPumpIteration(postInputSepData)).pipe(
            map((data) => {
              return pumpActions?.setChargePump1Iteration(data)

            })
          );
        }
        return of(pumpActions?.setChargePump1Iteration([]));
      }
    )
  );
};

const loadChargePump2iterations = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("gasHandlerCalculation/setCP2CalculationData"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(map((state: RootState) => state?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.fluid?.regionList)),
      state$.pipe(map((state: RootState) => state?.fluid?.selectedRegion)),
      state$.pipe(
        map((state: RootState) => state?.gasHandler?.CP2CalculationData)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled))
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        basicTdhData,
        basicGasCalculation,
        region,
        regionList,
        selectedRegion,
        cp2Data,
        gasoutput,
        aiEnabled
      ]) => {
        if (
          basicGasCalculation &&
          basicGasCalculation?.useOfChgPmpNew === "1" &&
          basicGasCalculation?.chargePump2
        ) {
          const selRegion = regionList?.find((x: any) => x?.id === region);
          let BOPD = tab3Input?.bopd?.toString() || "0";
          let BWPD = tab3Input?.bwpd?.toString() || "0";
          let bottmWhole = tab3Input?.bottomHoleTemp?.toString() || "0";
          let compSPGr = tab3Input?.combinedSpGr?.toString() || "0";
          let GOR = tab3Input.gor?.toString() || "0";
          let GasComprs = tab3Input?.gasCompressibility?.toString() || "0";
          let gasDesign = basicGasCalculation?.gasDesign?.toString() || "0";
          let oilApi = tab3Input?.oilAPI?.toString() || "0";
          let PIP = basicTdhData?.pip?.toString() || "0";
          let prodRate = tab2Input?.productionRate?.toString() || "0";
          let spGrGas = tab3Input?.spGrGas?.toString() || "0";
          let spGrOil = tab3Input?.spGrOil?.toString() || "0";
          let spGrWatr = tab3Input?.spGrWater?.toString() || "0";
          let tubingOD = tab1Input?.tubingOD?.toString() || "0";
          let watrCut = tab3Input?.waterCut?.toString() || "0";
          let bopdUnit = tab3Input?.unitBOPDListVal;
          let bwpdUnit = tab3Input?.unitBWPDListVal;
          let bottmWholeUnit = tab3Input?.unitBottomHoleTempListVal;
          let gasAvail = tab3Input?.gasAvailblty?.toString() || "0";
          let bubbPres = tab3Input?.bubblePoint?.toString() || "0";

          if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
          }
          let GORUnit = tab3Input?.unitGORListVal;
          let prodctnRateUnit = tab2Input?.unitProductionRateListVal;
          let unitSys = tab1Input?.unitSys;
          let casingPres = tab2Input?.casingPressure?.toString() || "0";
          let casingPresUnit =
            tab2Input?.unitCasingPressureListVal?.toString() || "";
          let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
          let perfrtn = tab2Input?.perforation?.toString() || "0";
          let perfrtnUnit = tab2Input?.unitPerforationListVal;
          let bubbPresUnit = tab3Input?.unitBubblePointListVal;
          let pumpSetng = tab2Input?.tvd === null ? "0" : tab2Input?.tvd;
          let pumpSetngUnit = tab2Input?.unitTVDListVal;
          let pmpID = tab1Input?.hiddenPumpID ?? "0";
          let desrdNoOfStge = cp2Data?.desiredStages?.toString();
          if (
            desrdNoOfStge === "" ||
            desrdNoOfStge == null ||
            desrdNoOfStge === undefined
          ) {
            desrdNoOfStge = "0";
          }
          let headPrStage = cp2Data?.liftPerStage ?? "0";
          let tempUnit = tab1Input?.hdnUnitMotor_T1OperatingTemp;
          let GasAvail = tab3Input?.gasAvailblty?.toString() || "";
          let prodRateUnit = tab2Input?.unitProductionRateListVal;

          let flowCorrection = +gasoutput?.CP2_FlowCorrection / 100 || 0;
          let powerCorrection = +gasoutput?.CP2_PowerCorrection / 100 || 0;
          let headCorrection = +gasoutput?.CP2_HeadCorrection / 100 || 0;

          let pmpEff = +(cp2Data?.efficiency ?? 0);
          let isGasVAl = GasAvail === "1";
          let opFreq = tab3Input?.operatngFreq;
          let preTFV = "";
          let finPIP = "";
          let temp = "";
          let BG = "";
          let GasVolm = "";

          const noOfGasSeps = basicGasCalculation?.noOfGasSeps;
          const gasSeperator = basicGasCalculation?.sepSelcted;
          if (basicGasCalculation?.chargePump) {
            preTFV = gasoutput?.CP1_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.CP1_Pressure?.toString() ?? "";
            temp = gasoutput?.CP1_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.CP1_Bg?.toString() ?? "";
            GasVolm = gasoutput?.CP1_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.CP1_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.CP1_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.CP1_FinalFLOP ?? FLOP);
          } else if (
            basicGasCalculation?.useOfChgPmp &&
            basicGasCalculation?.chargePumpGKX
          ) {
            preTFV = gasoutput?.GKX_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.GKX_Pressure?.toString() ?? "";
            temp = gasoutput?.GKX_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.GKX_Bg?.toString() ?? "";
            GasVolm = gasoutput?.GKX_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.GKX_GOR.toString() ?? GOR;
            compSPGr =
              gasoutput?.GKX_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.GKX_FinalFLOP ?? FLOP);
          } else if (+noOfGasSeps > 0 && gasSeperator) {
            if (noOfGasSeps === "1") {
              preTFV = gasoutput?.SP1_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP1_Pressure?.toString() ?? "";
              temp = gasoutput?.SP1_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP1_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP1_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP1_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP1_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP1_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "2") {
              preTFV = gasoutput?.SP2_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP2_Pressure?.toString() ?? "";
              temp = gasoutput?.SP2_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP2_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP2_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP2_GOR.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP2_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP2_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "3") {
              preTFV = gasoutput?.SP3_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP3_Pressure?.toString() ?? "";
              temp = gasoutput?.SP3_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP3_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP3_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP3_GOR.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP3_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP3_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "4") {
              preTFV = gasoutput?.SP4_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP4_Pressure?.toString() ?? "";
              temp = gasoutput?.SP4_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP4_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP4_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP4_GOR.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP4_FinalComposite_SpGr.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP4_FinalFLOP ?? FLOP);
            }
          } else {
            preTFV = gasoutput?.Intake_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.Intake_Pressure?.toString() ?? "";
            temp = gasoutput?.Intake_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.Intake_Bg?.toString() ?? "";
            GasVolm = gasoutput?.Intake_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.Intake_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.Intake_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.Intake_FinalFLOP ?? FLOP);
          }

          let pumpId = basicGasCalculation?.chargePump2;
          let regionName = selRegion?.name ?? selectedRegion ?? '';
          let staticBHHd = 0;
          let inflowType = +(tab2Input?.inflowTypeLstVal || 0);
          let isGasHandler = false;
          let AIEnabled = aiEnabled;
          let staticBHP = tab2Input?.sbhpForPressre ?? 0;
          if (inflowType === 0) {
            staticBHP = tab2Input?.reservoirPressure;
            staticBHHd = tab2Input?.staticFluidLvl;
          } else if (inflowType === 2) {
            staticBHHd = tab2Input?.staticFluidLvlForHd;
          }

          let postInputSepData: any = [
            BOPD,
            BWPD,
            bottmWhole,
            compSPGr,
            GOR,
            gasDesign,
            oilApi,
            PIP,
            prodRate,
            spGrGas,
            spGrOil,
            spGrWatr,
            tubingOD,
            watrCut,
            bopdUnit,
            bwpdUnit,
            bottmWholeUnit,
            GORUnit,
            prodctnRateUnit,
            unitSys,
            casingPres,
            casingPresUnit,
            FLOP,
            GasComprs,
            isGasVAl,
            perfrtn,
            perfrtnUnit,
            bubbPres,
            bubbPresUnit,
            pumpSetng,
            pumpSetngUnit,
            preTFV,
            finPIP,
            pmpID,
            desrdNoOfStge,
            headPrStage,
            prodRateUnit,
            pmpEff,
            temp,
            tempUnit,
            opFreq,
            BG,
            GasVolm,
            regionName,
            staticBHHd,
            inflowType,
            staticBHP,
            pumpId,
            flowCorrection,
            headCorrection,
            powerCorrection,
            isGasHandler,
            AIEnabled
          ];

          return from(pumpService?.loadPumpIteration(postInputSepData)).pipe(
            map((data) => {
              return pumpActions?.setChargePump2Iteration(data)

            })
          );
        }
        return of(pumpActions?.setChargePump2Iteration([]));
      }
    )
  );
};

const loadChargePump3iterations = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("gasHandlerCalculation/setCP3CalculationData"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(
        map((state: RootState) => state?.basicCalculations?.basicTDHCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(map((state: RootState) => state?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.fluid?.regionList)),
      state$.pipe(map((state: RootState) => state?.fluid?.selectedRegion)),
      state$.pipe(
        map((state: RootState) => state?.gasHandler?.CP3CalculationData)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(map((state: RootState) => state?.calculations?.aiEnabled))
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        basicTdhData,
        basicGasCalculation,
        region,
        regionList,
        selectedRegion,
        cp3Data,
        gasoutput,
        aiEnabled
      ]) => {
        if (
          basicGasCalculation &&
          basicGasCalculation?.useOfChgPmpNew === "1" &&
          basicGasCalculation?.chargePump3
        ) {
          const selRegion = regionList?.find((x: any) => x?.id === region);
          let BOPD = tab3Input?.bopd?.toString() || "0";
          let BWPD = tab3Input?.bwpd?.toString() || "0";
          let bottmWhole = tab3Input?.bottomHoleTemp?.toString() || "0";
          let compSPGr = tab3Input?.combinedSpGr?.toString() || "0";
          let GOR = tab3Input?.gor?.toString() || "0";
          let GasComprs = tab3Input?.gasCompressibility?.toString() || "0";
          let gasDesign = basicGasCalculation?.gasDesign?.toString() || "0";
          let oilApi = tab3Input?.oilAPI?.toString() || "0";
          let PIP = basicTdhData?.pip?.toString() || "0";
          let prodRate = tab2Input?.productionRate?.toString() || "0";
          let spGrGas = tab3Input?.spGrGas?.toString() || "0";
          let spGrOil = tab3Input?.spGrOil?.toString() || "0";
          let spGrWatr = tab3Input?.spGrWater?.toString() || "0";
          let tubingOD = tab1Input?.tubingOD?.toString() || "0";
          let watrCut = tab3Input?.waterCut?.toString() || "0";
          let bopdUnit = tab3Input?.unitBOPDListVal;
          let bwpdUnit = tab3Input?.unitBWPDListVal;
          let bottmWholeUnit = tab3Input?.unitBottomHoleTempListVal;
          let gasAvail = tab3Input?.gasAvailblty?.toString() || "0";
          let bubbPres = tab3Input?.bubblePoint?.toString() || "0";

          if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
          }
          let GORUnit = tab3Input?.unitGORListVal;
          let prodctnRateUnit = tab2Input?.unitProductionRateListVal;
          let unitSys = tab1Input?.unitSys;
          let casingPres = tab2Input?.casingPressure?.toString() || "0";
          let casingPresUnit =
            tab2Input?.unitCasingPressureListVal?.toString() || "";
          let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
          let perfrtn = tab2Input?.perforation?.toString() || "0";
          let perfrtnUnit = tab2Input?.unitPerforationListVal;
          let bubbPresUnit = tab3Input?.unitBubblePointListVal;
          let pumpSetng = tab2Input?.tvd === null ? "0" : tab2Input?.tvd;
          let pumpSetngUnit = tab2Input?.unitTVDListVal;
          let pmpID = tab1Input?.hiddenPumpID ?? "0";
          let desrdNoOfStge = cp3Data?.desiredStages?.toString();
          if (
            desrdNoOfStge === "" ||
            desrdNoOfStge == null ||
            desrdNoOfStge === undefined
          ) {
            desrdNoOfStge = "0";
          }
          let headPrStage = cp3Data?.liftPerStage ?? "0";
          let tempUnit = tab1Input?.hdnUnitMotor_T1OperatingTemp;
          let GasAvail = tab3Input?.gasAvailblty?.toString() || "";
          let prodRateUnit = tab2Input?.unitProductionRateListVal;

          let flowCorrection = +gasoutput?.CP3_FlowCorrection / 100 || 0;
          let powerCorrection = +gasoutput?.CP3_PowerCorrection / 100 || 0;
          let headCorrection = +gasoutput?.CP3_HeadCorrection / 100 || 0;

          let pmpEff = +(cp3Data?.efficiency ?? 0);
          let isGasVAl = GasAvail === "1";
          let opFreq = tab3Input?.operatngFreq;
          let preTFV = "";
          let finPIP = "";
          let temp = "";
          let BG = "";
          let GasVolm = "";

          const noOfGasSeps = basicGasCalculation?.noOfGasSeps;
          const gasSeperator = basicGasCalculation?.sepSelcted;
          if (basicGasCalculation?.chargePump2) {
            preTFV = gasoutput?.CP2_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.CP2_Pressure?.toString() ?? "";
            temp = gasoutput?.CP2_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.CP2_Bg?.toString() ?? "";
            GasVolm = gasoutput?.CP2_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.CP2_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.CP2_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.CP2_FinalFLOP ?? FLOP);
          } else if (basicGasCalculation?.chargePump) {
            preTFV = gasoutput?.CP1_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.CP1_Pressure?.toString() ?? "";
            temp = gasoutput?.CP1_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.CP1_Bg?.toString() ?? "";
            GasVolm = gasoutput?.CP1_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.CP1_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.CP1_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.CP1_FinalFLOP ?? FLOP);
          } else if (
            basicGasCalculation?.useOfChgPmp &&
            basicGasCalculation?.chargePumpGKX
          ) {
            preTFV = gasoutput?.GKX_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.GKX_Pressure?.toString() ?? "";
            temp = gasoutput?.GKX_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.GKX_Bg?.toString() ?? "";
            GasVolm = gasoutput?.GKX_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.GKX_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.GKX_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.GKX_FinalFLOP ?? FLOP);
          } else if (+noOfGasSeps > 0 && gasSeperator) {
            if (noOfGasSeps === "1") {
              preTFV = gasoutput?.SP1_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP1_Pressure?.toString() ?? "";
              temp = gasoutput?.SP1_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP1_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP1_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP1_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP1_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP1_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "2") {
              preTFV = gasoutput?.SP2_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP2_Pressure?.toString() ?? "";
              temp = gasoutput?.SP2_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP2_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP2_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP2_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP2_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP2_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "3") {
              preTFV = gasoutput?.SP3_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP3_Pressure?.toString() ?? "";
              temp = gasoutput?.SP3_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP3_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP3_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP3_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP3_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP3_FinalFLOP ?? FLOP);
            } else if (noOfGasSeps === "4") {
              preTFV = gasoutput?.SP4_TotalFluidVolume?.toString() ?? "";
              finPIP = gasoutput?.SP4_Pressure?.toString() ?? "";
              temp = gasoutput?.SP4_FinalTemperature?.toString() ?? "";
              BG = gasoutput?.SP4_Bg?.toString() ?? "";
              GasVolm = gasoutput?.SP4_VolumeOfGas?.toString() ?? "";
              GOR = gasoutput?.SP4_GOR?.toString() ?? GOR;
              compSPGr =
                gasoutput?.SP4_FinalComposite_SpGr?.toString() ?? compSPGr;
              FLOP = +(gasoutput?.SP4_FinalFLOP ?? FLOP);
            }
          } else {
            preTFV = gasoutput?.Intake_TotalFluidVolume?.toString() ?? "";
            finPIP = gasoutput?.Intake_Pressure?.toString() ?? "";
            temp = gasoutput?.Intake_FinalTemperature?.toString() ?? "";
            BG = gasoutput?.Intake_Bg?.toString() ?? "";
            GasVolm = gasoutput?.Intake_VolumeOfGas?.toString() ?? "";
            GOR = gasoutput?.Intake_GOR?.toString() ?? GOR;
            compSPGr =
              gasoutput?.Intake_FinalComposite_SpGr?.toString() ?? compSPGr;
            FLOP = +(gasoutput?.Intake_FinalFLOP ?? FLOP);
          }

          let pumpId = basicGasCalculation?.chargePump3;
          let regionName = selRegion?.name ?? selectedRegion ?? '';
          let staticBHHd = 0;
          let inflowType = +(tab2Input?.inflowTypeLstVal || 0);
          let isGasHandler = false;
          let AIEnabled = aiEnabled;

          let staticBHP = tab2Input?.sbhpForPressre ?? 0;
          if (inflowType === 0) {
            staticBHP = tab2Input?.reservoirPressure;
            staticBHHd = tab2Input?.staticFluidLvl;
          } else if (inflowType === 2) {
            staticBHHd = tab2Input?.staticFluidLvlForHd;
          }

          let postInputSepData: any = [
            BOPD,
            BWPD,
            bottmWhole,
            compSPGr,
            GOR,
            gasDesign,
            oilApi,
            PIP,
            prodRate,
            spGrGas,
            spGrOil,
            spGrWatr,
            tubingOD,
            watrCut,
            bopdUnit,
            bwpdUnit,
            bottmWholeUnit,
            GORUnit,
            prodctnRateUnit,
            unitSys,
            casingPres,
            casingPresUnit,
            FLOP,
            GasComprs,
            isGasVAl,
            perfrtn,
            perfrtnUnit,
            bubbPres,
            bubbPresUnit,
            pumpSetng,
            pumpSetngUnit,
            preTFV,
            finPIP,
            pmpID,
            desrdNoOfStge,
            headPrStage,
            prodRateUnit,
            pmpEff,
            temp,
            tempUnit,
            opFreq,
            BG,
            GasVolm,
            regionName,
            staticBHHd,
            inflowType,
            staticBHP,
            pumpId,
            flowCorrection,
            headCorrection,
            powerCorrection,
            isGasHandler,
            AIEnabled
          ];

          return from(pumpService?.loadPumpIteration(postInputSepData)).pipe(
            map((data) => {
              return pumpActions?.setChargePump3Iteration(data)

            })
          );
        }
        return of(pumpActions?.setChargePump3Iteration([]));
      }
    )
  );
};

const loadPumpPressureShaft = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated", "tubing/setHiddenHousingType"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(map((state: RootState) => state?.pump?.SelectedPump)),
      state$.pipe(
        map(
          (state: RootState) => state?.gasCalculations?.GasFinalCalculatedData
        )
      )
    ),
    switchMap(([actionData, tab1Input, pumpInput, selectedPump, finalData]) => {
      if (pumpInput && finalData) {
        let pmpId = tab1Input?.hiddenPumpID ?? "";

        let housingType = tab1Input?.hdn_Pump_HousingType ?? "";
        let noOfStgs =
          housingType === "1"
            ? selectedPump?.numberOfStages
            : pumpInput?.pump_DesrdStges?.toString();

        let liqPhseDens = finalData?.LiquidPhaseDensity ?? "";
        let liqPhseDensUnit = tab1Input?.hdnUnitLiqPhseDens;
        let unitSys = tab1Input?.unitSys;

        if (noOfStgs) {
          return from(
            pumpService?.loadPumpMaxCalc(
              pmpId,
              noOfStgs,
              liqPhseDens,
              housingType,
              liqPhseDensUnit,
              unitSys?.toString()
            )
          ).pipe(
            mergeMap((data) => {
              const pumpPressureShaft: PumpPressureShaft = {
                HousingPressureLimit: data[1],
                Pmax: data[0],
              };
              return [
                pumpActions?.setPumpPressureShaft(pumpPressureShaft),
                pumpActions?.setPumpCalculationComplete(true),
              ];
            })
          );
        }
      }
      return [pumpActions?.setPumpCalculationComplete(true)];
    })
  );
};

const loadChartHeaders = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated"),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const { tubing, well } = state;

      const postData = [
        well?.productionRate,
        well?.unitProductionRateListVal,
        tubing?.unitSys,
        2,
      ];

      return from(pumpService?.converToOutUnit(postData)).pipe(
        mergeMap((data) => {
          return [pumpActions?.setPumpChart1Header({
            pmpChrtPrdRt: data[0],
            pmpChrtPrdRtUnit: data[1],
          })]
        }
        ),
        catchError(() => of(console.error("Failed to load chart headers")))
      );
    })
  );
};

const loadChart1Data = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.GasFinalCalculatedData)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated))
    ),
    switchMap(([actionData, tab1Input, tab2Input, tab3Input, pumpInput, finalGasOutputs, pumpInputCalculated]) => {
      if (tab1Input?.hiddenPumpID && pumpInputCalculated && finalGasOutputs) {
        const pmpID = tab1Input?.hiddenPumpID ?? null;
        const noOfStgs = +pumpInput?.pump_DesrdStges;
        const unitSys = +tab1Input?.unitSys;
        const fldSpGravity = +finalGasOutputs?.compSPGr;
        const fldViscty = +tab3Input?.fluidViscosity;
        const opFreq = +tab3Input?.operatngFreq;
        const motrSpd = +pumpInput?.pump_FullLoadSp;
        const prodRate = +finalGasOutputs?.preTFV;
        const prodRateUnit = +tab2Input?.unitProductionRateListVal;
        const fldVisctyUnit = +tab3Input?.unitFluidViscosityListVal;
        const FinalDTH = +pumpInputCalculated?.pmpFinalTDH?.toString();
        const flowCorrection = +pumpInputCalculated?.flwCrrctnForPump / 100;
        const headCorrection = +pumpInputCalculated?.hdCrrctnForPump / 100;
        const powerCorrection = +pumpInputCalculated?.pWRCrrctnForPump / 100;

        return from(
          pumpService.LoadPlot1Graph(
            pmpID,
            noOfStgs,
            flowCorrection,
            headCorrection,
            powerCorrection,
            fldSpGravity,
            fldViscty,
            motrSpd,
            prodRate,
            unitSys,
            prodRateUnit,
            opFreq,
            fldVisctyUnit,
            FinalDTH
          )
        ).pipe(
          mergeMap((data) => {
            return [pumpActions?.setPumpChart1Data(data)];
          })
        );
      }
      return of(pumpActions?.setPumpChart1Data([]));
    })
  );
};

const loadChart2Data = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.GasFinalCalculatedData)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated))
    ),
    switchMap(([actionData, tab1Input, tab2Input, tab3Input, pumpInput, calculatedGasData, pumpInputCalculated]) => {

      if (tab1Input?.hiddenPumpID && pumpInputCalculated && calculatedGasData) {
        let pmpID = tab1Input?.hiddenPumpID ?? null
        let noOfStgs = +pumpInput?.pump_DesrdStges
        let unitSys = +tab1Input?.unitSys
        const fldSpGravity = +tab3Input?.combinedSpGr
        const fldViscty = +(tab3Input?.fluidViscosity ?? 0)
        const opFreq = +tab3Input?.operatngFreq;
        const motrSpd = +pumpInput?.pump_FullLoadSp
        const prodRate = +calculatedGasData?.preTFV
        const prodRateUnit = +tab2Input?.unitProductionRateListVal
        const fldVisctyUnit = +tab3Input?.unitFluidViscosityListVal
        const FinalDTH = +pumpInputCalculated?.pmpFinalTDH
        const flowCorrection = +pumpInputCalculated?.flwCrrctnForPump / 100;
        const headCorrection = +pumpInputCalculated?.hdCrrctnForPump / 100;
        const powerCorrection = +pumpInputCalculated?.pWRCrrctnForPump / 100;

        return from(pumpService.LoadPlot2Graph(pmpID, noOfStgs, flowCorrection, headCorrection, powerCorrection, fldViscty, motrSpd, prodRate, unitSys, prodRateUnit, opFreq, fldVisctyUnit, FinalDTH)).pipe(
          mergeMap((data) => {
            return [pumpActions?.setPumpChart2Data(data)]
          }
          ),
        )
      }
      return of(pumpActions?.setPumpChart2Data([]))
    })
  )
};

const loadChart3Data = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpInputCalculated"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(map((state: RootState) => state?.gasCalculations?.GasFinalCalculatedData)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated))
    ),
    switchMap(([actionData, tab1Input, tab2Input, tab3Input, pumpInput, calculatedGasData, pumpInputCalculated]) => {

      if (tab1Input?.hiddenPumpID && pumpInputCalculated && calculatedGasData) {

        let pmpID = tab1Input?.hiddenPumpID ?? null
        let noOfStgs = +pumpInput?.pump_DesrdStges
        let unitSys = +tab1Input?.unitSys
        const fldSpGravity = +tab3Input?.combinedSpGr
        const fldViscty = +(tab3Input?.fluidViscosity ?? 0)
        const opFreq = +tab3Input?.operatngFreq;
        const motrSpd = +pumpInput?.pump_FullLoadSp
        const prodRate = +calculatedGasData?.preTFV
        const prodRateUnit = +tab2Input?.unitProductionRateListVal
        const fldVisctyUnit = +tab3Input?.unitFluidViscosityListVal
        const FinalDTH = +pumpInputCalculated?.pmpFinalTDH
        const flowCorrection = +pumpInputCalculated?.flwCrrctnForPump / 100;
        const headCorrection = +pumpInputCalculated?.hdCrrctnForPump / 100;
        const powerCorrection = +pumpInputCalculated?.pWRCrrctnForPump / 100;

        return from(pumpService?.LoadPlot3Graph(pmpID, noOfStgs, flowCorrection, headCorrection, powerCorrection, fldViscty, fldSpGravity, motrSpd, prodRate, unitSys, prodRateUnit, opFreq, fldVisctyUnit, FinalDTH)).pipe(
          map(data => {
            return pumpActions?.setPumpChart3Data(data)
          }
          ),
        )
      }
      return of(pumpActions?.setPumpChart3Data([]))

    })
  )
};

const loadCompositeChart1Data = (action$: any, state$: any) => {
  return action$.pipe(
    ofType(
      "pump/setPumpInputCalculated",
      "pump/setPumpCompositeOverrideCorrectionFactors"
    ),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInput)),
      state$.pipe(
        map(
          (state: RootState) => state?.gasCalculations?.GasFinalCalculatedData
        )
      ),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated)),
      state$.pipe(
        map((state: RootState) => state?.pump?.CompPumpInputCalculated)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.BasicGasCalculation)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasCalculations?.GasCalculationOutPut)
      ),
      state$.pipe(
        map((state: RootState) => state?.gasHandler?.GKXCalculationData)
      )
    ),
    concatMap(
      ([
        actionData,
        tab1Input,
        tab2Input,
        tab3Input,
        pumpInput,
        finalGasOutputs,
        pumpInputCalculated,
        pumpComposteFactors,
        gasDetails,
        gasOutputs,
        gkxOutput,
      ]) => {
        if (
          tab1Input?.hiddenPumpID &&
          pumpInputCalculated &&
          pumpComposteFactors &&
          finalGasOutputs
        ) {
          let pmpID = tab1Input?.hiddenPumpID ?? null;
          let noOfStgs = +pumpInput?.pump_DesrdStges;
          let unitSys = +tab1Input?.unitSys;
          const fldSpGravity = +finalGasOutputs?.compSPGr;
          const fldViscty = +tab3Input?.fluidViscosity;
          const opFreq = +tab3Input?.operatngFreq;
          const motrSpd = +pumpInput?.pump_FullLoadSp || 3500;
          const prodRateUnit = +tab2Input?.unitProductionRateListVal;
          const fldVisctyUnit = +tab3Input?.unitFluidViscosityListVal;
          const FinalDTH = +pumpInputCalculated?.pmpFinalTDH?.toString();
          const flowCorrection =
            +pumpComposteFactors?.compositeFlowCorrectionFactor / 100;
          const headCorrection =
            +pumpComposteFactors?.compositeHeadCorrectionFactor / 100;
          const powerCorrection =
            +pumpComposteFactors?.compositePowerCorrectionFactor / 100;
          const GKXpumpId = gasDetails?.chargePumpGKX ?? "";
          const GKXNumberOfStage = +gkxOutput?.desiredStages;
          const ChargePump1Id = gasDetails?.chargePump ?? null;
          const ChargePump2Id = gasDetails?.chargePump2 ?? null;
          const ChargePump3Id = gasDetails?.chargePump3 ?? null;
          const ChargePmp1NoOfStage = +gasDetails?.chargePumpDesrdStge;
          const ChargePmp2NoOfStage = +gasDetails?.chargePumpDesrdStge2;
          const ChargePmp3NoOfStage = +gasDetails?.chargePumpDesrdStge3;
          let prodRate = 0;
          let fluidSpGravity = 0;

          const noOfGasSeps = +(gasDetails?.noOfGasSeps || 0);
          if (noOfGasSeps === 0) {
            prodRate = +(gasOutputs?.Intake_TotalFluidVolume || 0);
            fluidSpGravity = +(gasOutputs?.Intake_FinalComposite_SpGr || 0);
          } else {
            prodRate = +(gasOutputs?.SP1_TotalFluidVolume || 0);
            fluidSpGravity = +(gasOutputs?.SP1_FinalComposite_SpGr || 0);
          }

          const ComPositepostData_Obj = {
            Mainpumpid: pmpID,
            MainPmpNumberOfStages: noOfStgs,
            CompositePumpFlowCorrection: flowCorrection,
            CompositePumpHeadCorrection: headCorrection,
            CompositePumpPowerCorrection: powerCorrection,
            CompositeSpGravity: fldSpGravity,
            Viscosity: fldViscty,
            ViscosityUnit: fldVisctyUnit,
            Operatingfrequency: opFreq,
            MotorSpeed: motrSpd,
            ProductionRate: prodRate,
            ProductionrateUnit: prodRateUnit,
            Unitsystem: unitSys,
            FinalTDH: FinalDTH,
            GKXpumpId: GKXpumpId,
            FluidSpGravity: fluidSpGravity,
            GKXNumberOfStage: GKXNumberOfStage,
            ChargePump1Id: ChargePump1Id,
            ChargePump2Id: ChargePump2Id,
            ChargePump3Id: ChargePump3Id,
            ChargePmp1NoOfStage: ChargePmp1NoOfStage,
            ChargePmp2NoOfStage: ChargePmp2NoOfStage,
            ChargePmp3NoOfStage: ChargePmp3NoOfStage,
          };
          return from(
            pumpService?.LoadCompositeGraph(ComPositepostData_Obj)
          ).pipe(
            mergeMap((data) => {
              return [pumpActions?.setCompositePumpChartData(data)];
            })
          );
        }
        return of(pumpActions?.setCompositePumpChartData([]));
      }
    )
  );
};

const fetchEquipments = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/fetchEquipments"),
    withLatestFrom(state$),
    distinctUntilChanged(([prevAction, prevState], [currAction, currState]) => {
      return JSON.stringify(prevState) === JSON.stringify(currState);
    }),
    switchMap(([, state]) => {
      const { tubing, pump } = state;
      return from(
        pumpService.fetchEquipmentsForPumps(
          pump?.SelectedPump?.pumpName,
          tubing?.tab1Input?.catalogType
        )
      ).pipe(
        mergeMap((data) => {
          if (data?.data?.length === 0) {
            return [
              setGasNotification({
                show: true,
                message: "There are no co-related Equipments for the selected Pump",
                type: "info",
              }),
              pumpActions?.setEquipmentsForPumps(data.data)
            ]
          }
          return of(pumpActions?.setEquipmentsForPumps(data.data));
        }),
        catchError((error) => {
          console.error("Error fetching equipments:", error);
          return of({ type: "" });
        })
      );
    })
  );
};

const GetShaftRating = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setSelectedPump", "tubing/setHiddenShaftType"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
      state$.pipe(map((state: RootState) => state?.pump?.SelectedPump))
    ),
    switchMap(([actionData, tab1Input, tab3Input, selectedPump]) => {
      let shaftType = tab1Input?.hdn_Pump_ShaftType;
      let operatingFrequency = tab3Input?.operatngFreq ?? 0;
      let shaftPowerLimit =
        shaftType === "0"
          ? selectedPump?.standardShaftHPLimit
          : selectedPump?.hsShaftHPLimit;
      if (shaftPowerLimit) {
        let result = (parseFloat(shaftPowerLimit) * operatingFrequency) / 60;
        return [pumpActions?.setFetchShaftRating(result?.toFixed(1))];
      }
      return [pumpActions?.setFetchShaftRating("")];
    })
  );
};

const GetShaftPercentageLoad = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setFetchShaftRating", "pump/setPumpInputCalculated"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.FetchShaftRating)),
      state$.pipe(map((state: RootState) => state?.pump?.PumpInputCalculated))
    ),
    switchMap(([actionData, shaftValue, pumpInputCalculated]) => {
      if (shaftValue !== "" && pumpInputCalculated?.hPOPPump !== "") {
        let shfRatValVal1 = parseFloat(shaftValue?.toString());
        let pwr60HzVal1 = parseFloat(pumpInputCalculated?.hPOPPump);
        let prCntShftLdVal = ((pwr60HzVal1 / shfRatValVal1) * 100).toFixed(2);
        return [pumpActions?.setFetchShaftPercentageLoad(prCntShftLdVal)];
      }
      return [pumpActions?.setFetchShaftPercentageLoad("")];
    })
  );
};

const GetPercentageHousingLoad = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setPumpPressureShaft"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.pump?.PumpPressureShaft))
    ),
    switchMap(([actionData, pumpPressureShaft]) => {
      let pMaxVal1 = parseFloat(pumpPressureShaft?.Pmax);
      let HsngPrsLmtVal1 = parseFloat(pumpPressureShaft?.HousingPressureLimit);
      if (HsngPrsLmtVal1 !== 0) {
        let prCntHsngLdVal = ((pMaxVal1 / HsngPrsLmtVal1) * 100).toFixed(2);
        return [pumpActions?.setFetchPercentageHousingLoad(prCntHsngLdVal)];
      }
      return [pumpActions?.setFetchPercentageHousingLoad("")];
    })
  );
};

const fetchBOHEquipments = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/fetchBOHEquipments", "pump/setSelectedPump"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing)),
      state$.pipe(map((state: RootState) => state?.pump))
    ),
    distinctUntilChanged(([prevAction, prevState], [currAction, currState]) => {
      return JSON.stringify(prevState) === JSON.stringify(currState);
    }),
    concatMap(([actionData, tubing, pump]) => {
      let pumpName = pump?.SelectedPump
      ? pump?.SelectedPump
        : pump?.selectedPumpName;
        if (!pumpName?.pumpName) {
          return of({ type: "" });
        }
        return from(
          pumpService.fetchBOHEquipments(pumpName, tubing.tab1Input?.tubingOD ?? 0)
        ).pipe(
          mergeMap((data) => {
          if ((data as unknown as any[]).length === 0) {
            return [
              setGasNotification({
                show: true,
                message: "There are no BOH co-related Equipments for the selected Pump",
                type: "info",
              }),
              pumpActions.setBOHEquipments([])
            ]
          }
          const newSeriesList = Array.isArray(data) ? data.map((item: any) => item?.series) : [];
          const existingSeriesList = Array.isArray(pump?.ESPEquipmentsBOH)
          ? pump.ESPEquipmentsBOH.map((item: any) => item?.series)
          : [];
          const hasSeriesDifference = newSeriesList.some((series, index) => existingSeriesList?.includes(series) === false);
          if (hasSeriesDifference) {
            return of( pumpActions.setESPEquipmentsForBOH([]), calculationActions?.fetchUpdatedSchematicEquipmentData(), pumpActions.setBOHEquipments(data));
          }
          return of(pumpActions.setBOHEquipments(data));
        }),
        catchError((error) => {
          console.error("Error fetching BOH equipments:", error);
          return of({ type: "" });
        })
      );
    })
  );
};


const UpdateShaftTypeForPump = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("pump/setFilteredEquipments", "pump/loadPumpHiddenShaftType"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
      state$.pipe(map((state: RootState) => state?.pump?.ESPEquipments)),
      state$.pipe(map((state: RootState) => state?.pump?.SelectedPump)),
    ),
    switchMap(([actionData, tab1Input, SelectedPumpEquipments, SelectedPump]) => {
      if ((tab1Input?.hiddenPumpID === SelectedPump?.pumpID) && (tab1Input?.hdn_Pump_ShaftType === '0' || tab1Input?.hdn_Pump_ShaftType === '1')) {
        setHiddenShaftType(tab1Input?.hdn_Pump_ShaftType);
      } else if (tab1Input?.hiddenPumpID === SelectedPumpEquipments[0]?.parentId && SelectedPumpEquipments?.length > 0) {
        const shaftType = SelectedPumpEquipments[SelectedPumpEquipments?.length - 1]?.shaftType === 'STD' ? 0 : 1;
        setHiddenShaftType(shaftType.toString());
      } else {
        setHiddenShaftType('1');
      }
      return EMPTY;
    })
  );
};


export const pumpEpic = combineEpics(
  loadPumpList,
  loadPumpiterations,
  loadGKXPumpiterations,
  loadChargePump1iterations,
  loadChargePump2iterations,
  loadChargePump3iterations,
  loadChartHeaders,
  loadChart1Data,
  loadCompositeChart1Data,
  loadPumpLabels,
  GetShaftRating,
  loadPumpLablesOnCorrectionFactorsOverride,
  loadChart2Data,
  loadChart3Data,
  fetchEquipments,
  fetchBOHEquipments,
  loadPumpPressureShaft,
  GetShaftPercentageLoad,
  GetPercentageHousingLoad,
  getPumpFreeGasWarning,
  UpdateShaftTypeForPump,
  calculationsPumpAIData,
  calculationsCP1AIData,
  calculationsCP2AIData,
  calculationsCP3AIData
);
